/**
 * What the Refresh button will do, and roughly what it costs.
 * For Media Devoted | Created By Ciro Autiero
 *
 * The count is an ESTIMATE and says so. An exact number is not knowable from
 * the browser: the tracker's reports are cached ~60s server-side and shared
 * with the scheduled auto-run, so most presses cost far less than the ceiling —
 * and nothing in the API reports whether a given window was a cache hit.
 * "About N–M" is the honest form; a bare number would be wrong most of the time.
 */

import type { FC } from 'react';
import { trackerLabel, type FlowOptimizerConfig } from '@trafficpulse/shared';

import { queueProviderOf } from '@/features/trackers/flow-optimizer/queue-provider';

import type { FlowOptimizerProvider } from '../services/flow-optimizer-api';
import { estimateRefreshCost } from './estimate-refresh-cost';

interface Props {
  config: FlowOptimizerConfig;
  provider: FlowOptimizerProvider;
  refreshesWeights: boolean;
  refreshesFlow: boolean;
  flowHeldByDirty: boolean;
}

const Item: FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="flex gap-1.5">
    <span aria-hidden>•</span>
    <span>{children}</span>
  </li>
);

export const RefreshTooltip: FC<Props> = ({
  config,
  provider,
  refreshesWeights,
  refreshesFlow,
  flowHeldByDirty,
}) => {
  const cost = estimateRefreshCost({ config, provider });
  const name = trackerLabel(provider);
  // Live status and rotation history come from the queue — none without one.
  const hasQueue = queueProviderOf(provider) !== null;
  return (
    <div className="space-y-1.5 text-left text-[11px] leading-relaxed">
      <p className="font-semibold">Re-read {name} without closing this window</p>
      <ul className="space-y-0.5">
        {refreshesWeights ? (
          <Item>
            <strong>Weights</strong> — current vs optimal weight, scores and tags
            for every path, lander and offer.
          </Item>
        ) : null}
        {hasQueue ? (
          <Item>
            <strong>Live status</strong> — which paths are still in the flow, and
            whether any is switched off or sitting at weight 0.
          </Item>
        ) : null}
        {hasQueue ? <Item>Rotation history and each element&apos;s timeline.</Item> : null}
        {refreshesFlow ? <Item>The Standard tab&apos;s flow.</Item> : null}
      </ul>
      <p>
        <strong>Cost: about {cost.min}–{cost.max} requests to {name}.</strong>{' '}
        Your formulas, rules and tags use{' '}
        <strong>{cost.windows} date range{cost.windows === 1 ? '' : 's'}</strong>,
        and each one costs about {cost.perExtraWindow} report calls. Add another
        date range and it costs {cost.perExtraWindow} more; re-use one you
        already have and it costs nothing.
      </p>
      <p className="text-muted-foreground">
        {name} results are cached for about a minute and shared with the
        scheduled run, so pressing this again straight away usually costs
        nothing. Nothing is written to {name}.
      </p>
      <p className="text-muted-foreground">
        Your unsaved Flow Optimizer settings are never touched.
        {flowHeldByDirty
          ? ' The Standard tab is not re-read either, because you have unsaved edits there.'
          : ''}
      </p>
    </div>
  );
};

export default RefreshTooltip;
